import React from 'react'
import { makeStyles } from 'tss-react/mui'
import {
  ContentfulType,
  CustomElementMap,
  ReferenceType,
  ReferenceTypeMap,
} from '../../types/references'
import TextInsert from '../ContentfulEntries/TextInsert'
import ImageInsert from '../ContentfulEntries/ImageInsert'
import CallToAction from '../CallToAction'
import CustomComponent from '../CustomComponent'
import RichTextErrorBoundary, { BlockOrInline } from './ErrorBoundary'
import Image from '../ContentfulEntries/Image'
import NavigationButton from '../Buttons/NavigationButton'
import { TextColumnInsert } from '../ContentfulEntries/TextColumnInsert'
import { TextColumnGroupInsert } from '../ContentfulEntries/TextColumnGroupInsert'
import StyleListInsert from '../ContentfulEntries/StyleListInsert'
import HoverTextComponent from '../ContentfulEntries/HoverTextComponent'
import TimeChip from '../ContentfulEntries/TimeChip'
import AccordionList from '../ContentfulEntries/AccordionList'
import ImageToggle from '../ContentfulEntries/ImageToggle'
import TabbedComponent from '../ContentfulEntries/TabbedComponent'
import VideoInsert from '../ContentfulEntries/VideoInsert'
import MediaInsert from '../ContentfulEntries/MediaInsert'
import InfoTable from '../Tables/InfoTable'
import RangeSlider from '../ContentfulEntries/RangeSlider'
import NavigationGroup from '../NavigationGroup'
import PriceInsert from '../ContentfulEntries/PriceInsert'
import RelatedPosts from '../RelatedPosts'
import DynamicText from '../ContentfulEntries/DynamicText'
import ReferenceTextCopy from '../ContentfulEntries/ReferenceTextCopy'
import TrustPilotRatingWidget from '../TrustPilot/TrustPilotRatingWidget'
import formatContentfulClasses from '../../utils/customClasses'
import ArticlesDisplay from '../Articles/ArticlesDisplay'
import CarouselSection from '../LandingPage/CarouselSection'
import { SuccessStoryDesc } from '../../types/contentfulAttributes'
import DynamicMatchHeaderAndDesc from '../Match/DynamicMatchHeaderAndDesc'
import { GenericGrid } from '../GenericGrid/GenericGrid'
import TRResumeUpload from '../Widgets/TRResumeUpload'
import { Debug } from '../Debug'

interface EmbeddedEntryProps {
  customElements: Partial<CustomElementMap>
  node: BlockOrInline
  children: React.ReactNode
  references: ReferenceType[]
}

/**
 * EmbeddedEntry is a generic component that renders a Contentful embedded entry
 * in rich text. It has a set of base components that render common embedded
 * entries that can be overridden by the caller by providing overrides to
 * `customElements`.
 */
const EmbeddedEntry: React.FC<EmbeddedEntryProps> = ({
  node,
  references,
  children,
  customElements,
}) => {
  const elements = { ...defaultCustomElements, ...customElements }
  const id = node?.data?.target?.sys?.id

  if (!id) {
    return (
      <Debug
        children={children}
        node={node}
        references={references}
        customElements={customElements}
      />
    )
  }

  const reference = findReference(id, references) as { __typename: string }

  if (!reference || !(reference.__typename in elements)) {
    return (
      <Debug
        children={children}
        node={node}
        references={references}
        customElements={customElements}
      />
    )
  }

  const element = (elements as any)[reference.__typename]

  return (
    <RichTextErrorBoundary node={node}>
      {element({
        node,
        //@ts-ignore
        reference: reference as ReferenceTypeMap[typeof reference.__typename],
        children,
      })}
    </RichTextErrorBoundary>
  )
}

export const defaultCustomElements: Partial<CustomElementMap> = Object.freeze({
  [ContentfulType.TextInsert]: ({ reference }) => (
    <TextInsert textInsert={reference} />
  ),
  [ContentfulType.ImageInsert]: ({ reference }) => (
    <ImageInsert imageInsert={reference} />
  ),
  [ContentfulType.CallToAction]: ({ reference }) => (
    <CallToAction cta={reference} inPost />
  ),
  [ContentfulType.Asset]: ({ reference }) => {
    const { classes, cx } = useStyles()

    return (
      <Image
        asset={reference}
        className={cx({
          [classes.postImage]: true,
          [classes.successStoryTitleImage]: reference.description.includes(
            SuccessStoryDesc.TitleImage
          ),
          [classes.successStoryCircleImage]: reference.description.includes(
            SuccessStoryDesc.CircleImage
          ),
        })}
      />
    )
  },
  [ContentfulType.Button]: ({ reference }) => {
    const { classes, cx } = useStyles()

    return (
      <NavigationButton
        className={cx(
          classes.button,
          ...formatContentfulClasses(reference.codeClasses)
        )}
        link={reference}
      />
    )
  },
  [ContentfulType.JumpLink]: ({ reference }) => (
    <span id={reference.hash} data-contentful-id={reference.contentful_id} />
  ),
  [ContentfulType.TextColumn]: ({ reference }) => (
    <TextColumnInsert textColumnInsert={reference} />
  ),
  [ContentfulType.TextColumnGroup]: ({ reference }) => (
    <TextColumnGroupInsert textColumnInsert={reference} />
  ),
  [ContentfulType.StyledList]: ({ reference }) => (
    <StyleListInsert list={reference} />
  ),
  [ContentfulType.CustomComponentType]: ({ reference }) => (
    <CustomComponent item={reference} variant="richText" />
  ),
  [ContentfulType.HoverTextComponent]: ({ reference }) => (
    <HoverTextComponent item={reference} />
  ),
  [ContentfulType.TimeChipComponent]: ({ reference }) => (
    <TimeChip chip={reference} />
  ),
  [ContentfulType.AccordionListComponent]: ({ reference }) => (
    <AccordionList list={reference} />
  ),
  [ContentfulType.ImageToggleComponent]: ({ reference }) => (
    <ImageToggle toggle={reference} />
  ),
  [ContentfulType.TabbedComponent]: ({ reference }) => (
    <TabbedComponent list={reference} />
  ),
  [ContentfulType.VideoInsertComponent]: ({ reference }) => (
    <VideoInsert insert={reference} />
  ),
  [ContentfulType.MediaInsertComponent]: ({ reference }) => (
    <MediaInsert insert={reference} />
  ),
  [ContentfulType.InfoTableComponent]: ({ reference }) => (
    <InfoTable table={reference} />
  ),
  [ContentfulType.RangeSliderComponent]: ({ reference }) => (
    <RangeSlider slider={reference} />
  ),
  [ContentfulType.NavigationGroupComponent]: ({ reference }) => {
    const { classes } = useStyles()

    return (
      <NavigationGroup
        groupClass={classes.navigationGroup}
        navigationGroup={reference}
      />
    )
  },
  [ContentfulType.PriceComponent]: ({ reference }) => (
    <PriceInsert item={reference}>${reference.price}</PriceInsert>
  ),
  [ContentfulType.PostCardsComponent]: ({ reference }) => {
    const { classes } = useStyles()

    return (
      <RelatedPosts
        links={reference.posts}
        sectionClass={classes.postsSection}
      />
    )
  },
  [ContentfulType.DynamicTextComponent]: ({ reference }) => (
    <DynamicText text={reference} />
  ),
  [ContentfulType.ReferencedTextCopy]: ({ reference }) => (
    <ReferenceTextCopy reference={reference} />
  ),
  [ContentfulType.TrustPilotRatingWidget]: ({ reference }) => (
    <TrustPilotRatingWidget reference={reference} />
  ),
  [ContentfulType.ArticlesDisplay]: ({ reference }) => (
    <ArticlesDisplay link={reference} />
  ),
  [ContentfulType.CarouselComponent]: ({ reference }) => (
    <CarouselSection carouselSection={reference} />
  ),
  [ContentfulType.MatchTitlesAndDesc]: ({ reference }) => (
    <DynamicMatchHeaderAndDesc matchTitlesAndDescriptions={reference} />
  ),
  [ContentfulType.GridComponent]: ({ reference }) => (
    <GenericGrid
      gridCells={reference.gridCells}
      desktop={reference.desktopColumns}
      tablet={reference.tabletColumns}
      laptop={reference.laptopColumns}
      spacing={reference.spaceBetweenCells}
      mobile={reference.mobileColumns}
    />
  ),
  [ContentfulType.ResumeUpload]: ({ reference }) => (
    <TRResumeUpload options={reference} />
  ),
})

export const findReference = (
  id: string,
  references: ReferenceType[] = []
): ReferenceType | undefined =>
  references.find((ref) => (ref as any).contentful_id === id)

const useStyles = makeStyles()((theme) => ({
  button: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  navigationGroup: {
    flexDirection: 'column',
    width: '100%',

    '& *': {
      margin: '.35em 0',
    },
  },
  postImage: {
    margin: '2em auto',
  },
  successStoryTitleImage: {
    '&&': {
      margin: '1.125rem 0 .125rem',
      maxWidth: '100%',
    },
  },
  successStoryCircleImage: {
    '&&': {
      margin: '0.375rem auto',
    },
  },
  postsSection: {
    backgroundColor: 'transparent',
    [theme.breakpoints.down('md')]: {
      flexWrap: 'unset',
    },
    padding: '0 6%',
    '@media (min-width: 1456px)': {
      paddingLeft: 'calc((100% - 91rem)/2 + 5.5rem)',
      paddingRight: 'calc((100% - 91rem)/2 + 5.5rem)',
    },
  },
}))

export default EmbeddedEntry
